import { RefObject, useCallback } from 'react'

import cn from 'classnames'
import { useTranslation } from 'react-i18next'

import { TRANSACTION_TYPE_LABELS, TRANSACTION_TYPES } from './constants'
import {
  TransactionType,
  TransactionTypeDropdownProps,
} from './types'
import { Dropdown, DropdownItem } from '../Dropdown'
import dropdownStyles from '../Dropdown/styles.module.scss'
import { DropdownRenderToggleArgs } from '../Dropdown/types'
import { Icon } from '../Icon'
import { IconStyles } from '../Icon/styles'

export const TransactionTypeDropdown = ({
  value,
  onChange,
  className,
  toggleClassName,
  id = 'transactionTypeFilter',
}: TransactionTypeDropdownProps) => {
  const { t } = useTranslation()
  const handleSelect = useCallback(
    (type?: TransactionType) => () => onChange(type),
    [onChange]
  )
  const isActive = useCallback(
    (type?: TransactionType) =>
      type ? value === type : value === undefined,
    [value]
  )

  const renderToggle = useCallback(
    ({ ref, toggle, isOpen }: DropdownRenderToggleArgs) => {
      const handleToggleClick = () => toggle()

      return (
        <button
          type="button"
          ref={ref as RefObject<HTMLButtonElement>}
          onClick={handleToggleClick}
          className={cn(dropdownStyles.toggle, toggleClassName, {
            [dropdownStyles.toggleActive]: Boolean(value),
            [dropdownStyles.toggleOpen]: isOpen,
          })}
          aria-haspopup="menu"
          aria-expanded={isOpen}
          id={id}
        >
          <span className={dropdownStyles.toggleLabel}>
            {value ? t(TRANSACTION_TYPE_LABELS[value]) : t('Chat.Filters.TransactionTypes')}
          </span>
          <span className={dropdownStyles.toggleIcon} aria-hidden>
            <Icon path="icons/icon-chevron" style={IconStyles.small} />
          </span>
        </button>
      )
    },
    [id, t, toggleClassName, value]
  )

  const renderContent = useCallback(
    () => (
      <>
        <DropdownItem onSelect={handleSelect(undefined)} active={isActive(undefined)}>
          {t('Chat.All')}
        </DropdownItem>
        {TRANSACTION_TYPES.map((type) => (
          <DropdownItem
            key={type}
            onSelect={handleSelect(type)}
            active={isActive(type)}
          >
            {t(TRANSACTION_TYPE_LABELS[type])}
          </DropdownItem>
        ))}
      </>
    ),
    [handleSelect, isActive, t]
  )

  return (
    <div className={className}>
      <Dropdown
        renderToggle={renderToggle}
        renderContent={renderContent}
        modalTitle={t('Chat.Filters.TransactionTypes')}
      />
    </div>
  )
}
